import { createSlice } from '@reduxjs/toolkit';
import { get, uniq } from 'lodash';

import { makeMockTourists } from 'src/utils/mockUtils';

import { changeTouristData, clearTouristData } from 'src/store/tourists/slice';
import { resetDefaultClientDataAction } from 'checkout/CheckoutRoutes/HotelCheckout/store/domains/client/actions';
import { createPackageSuccess } from '../actions/package';
import {
  setTouristValidatedAction,
  updateMockTouristAction,
} from '../actions/tourists';
import {
  deleteSavedTouristFailAction,
  deleteSavedTouristSuccessAction,
  setTouristsAction,
  updateSavedTouristAction,
} from '../client/slice';

import type { DataStateType } from '../data/types';

const initialState: DataStateType = {
  citizenships: [],
  holidaySchedule: null,
  expired: false,
  mockTourists: [],
  validatedTourists: [],
  tourists: [],
};

const getValidatedTourists = (state: DataStateType) =>
  get(state, 'validatedTourists') || [];

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(updateMockTouristAction, (state, { payload }) => {
        const { mockTourists } = state;
        const { index, visa_service } = payload;

        return {
          ...state,
          mockTourists: mockTourists.map((tourist, i) =>
            i === index ? { ...tourist, visa_service } : tourist,
          ),
        };
      })
      .addCase(setTouristsAction, (state, { payload }) => ({
        ...state,
        tourists: payload,
      }))
      .addCase(updateSavedTouristAction, (state, { payload }) => {
        const { tourists } = state;

        const indexToReplace = tourists.findIndex((t) => t.id === payload.id);
        const newTourists =
          indexToReplace === -1
            ? [...tourists]
            : [...tourists].splice(indexToReplace, 1, payload);

        return {
          ...state,
          tourists: newTourists,
        };
      })
      .addCase(deleteSavedTouristSuccessAction, (state, { payload }) => {
        const { tourists } = state;

        return {
          ...state,
          tourists: tourists.filter((t) => t.id !== payload),
        };
      })
      .addCase(deleteSavedTouristFailAction, (state, { payload }) => {
        if (payload === undefined) return state;
        const { tourists } = state;

        return {
          ...state,
          tourists: tourists.map((tourist, i) =>
            i === payload ? { ...tourist, state: 'error' } : tourist,
          ),
        };
      })
      .addCase(changeTouristData, (state, { payload }) => {
        const { index } = payload;
        const validatedTourists = getValidatedTourists(state).filter(
          (_, i) => i !== index,
        );

        return {
          ...state,
          validatedTourists,
        };
      })
      .addCase(setTouristValidatedAction, (state, { payload }) => {
        const validatedTourists = uniq([
          ...getValidatedTourists(state),
          payload,
        ]);

        return {
          ...state,
          validatedTourists,
        };
      })
      .addCase(createPackageSuccess, (state, { payload }) => ({
        ...state,
        mockTourists: makeMockTourists(payload),
      }))
      .addCase(clearTouristData, (state) => ({
        ...state,
        validatedTourists: [],
      }))
      .addCase(resetDefaultClientDataAction, (state, { payload }) => ({
        ...state,
        resetDefaultClientData: payload,
      })),
});

export default dataSlice.reducer;
